import React, { useRef, useState, useMemo } from 'react';
import { QRCodeCanvas } from 'qrcode.react';
import { useLanguage } from '../i18n';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import {
  Copy, Download, Share2, MessageSquare, Mail, QrCode,
  Link as LinkIcon, Smartphone
} from 'lucide-react';

export default function InvitePage() {
  const { t } = useLanguage();
  const canvasRef = useRef(null);
  const [customMsg, setCustomMsg] = useState(
    'Здравствуйте! Приглашаем стать партнёром CRAFTFOOD — производителя LAVASH (Modern Flatbread). Зарегистрируйтесь по ссылке и начните оформлять заказы:'
  );

  const inviteUrl = useMemo(() => {
    if (typeof window !== 'undefined') {
      return `${window.location.origin}/register`;
    }
    return '';
  }, []);

  const fullMessage = `${customMsg}\n\n${inviteUrl}`;

  const copyToClipboard = async (text, label = 'Скопировано') => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success(label);
    } catch {
      toast.error('Не удалось скопировать');
    }
  };

  const shareNative = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: 'CRAFTFOOD — приглашение',
          text: customMsg,
          url: inviteUrl,
        });
      } catch (_) { /* user cancelled */ }
    } else {
      copyToClipboard(fullMessage, 'Скопировано (системный share недоступен)');
    }
  };

  const downloadQR = () => {
    const canvas = canvasRef.current?.querySelector('canvas');
    if (!canvas) return;
    const url = canvas.toDataURL('image/png');
    const link = document.createElement('a');
    link.download = 'craftfood-invite-qr.png';
    link.href = url;
    link.click();
    toast.success('QR-код скачан');
  };

  const channels = [
    {
      key: 'sms',
      label: 'SMS',
      Icon: Smartphone,
      bg: 'bg-[#556B2F]',
      fg: 'text-[#F2EFE6]',
      action: () => {
        const url = `sms:?body=${encodeURIComponent(fullMessage)}`;
        window.open(url, '_blank');
      },
    },
    {
      key: 'whatsapp',
      label: 'WhatsApp',
      Icon: MessageSquare,
      bg: 'bg-[#25D366]',
      fg: 'text-white',
      action: () => {
        const url = `https://wa.me/?text=${encodeURIComponent(fullMessage)}`;
        window.open(url, '_blank');
      },
    },
    {
      key: 'telegram',
      label: 'Telegram',
      Icon: Share2,
      bg: 'bg-[#229ED9]',
      fg: 'text-white',
      action: () => {
        const url = `https://t.me/share/url?url=${encodeURIComponent(inviteUrl)}&text=${encodeURIComponent(customMsg)}`;
        window.open(url, '_blank');
      },
    },
    {
      key: 'kakao',
      label: 'KakaoTalk',
      Icon: MessageSquare,
      bg: 'bg-[#FEE500]',
      fg: 'text-[#1A1A1A]',
      action: () => {
        // KakaoTalk has no universal share URL — copy + open kakao web
        copyToClipboard(fullMessage, 'Сообщение скопировано — откройте KakaoTalk и вставьте');
        window.open('https://accounts.kakao.com', '_blank');
      },
    },
    {
      key: 'email',
      label: 'Email',
      Icon: Mail,
      bg: 'bg-[#1A1A1A]',
      fg: 'text-[#F6F4EE]',
      action: () => {
        const url = `mailto:?subject=${encodeURIComponent('CRAFTFOOD — приглашение к партнёрству')}&body=${encodeURIComponent(fullMessage)}`;
        window.location.href = url;
      },
    },
  ];

  return (
    <div className="space-y-8 max-w-6xl" data-testid="invite-page">
      <div>
        <p className="eyebrow mb-3">Партнёры</p>
        <h1 className="h-display text-4xl md:text-5xl">Пригласить <span className="text-[#C9A352]">партнёра</span></h1>
        <p className="text-[#1A1A1A]/55 mt-3 max-w-2xl">
          Поделитесь ссылкой и QR-кодом. После регистрации клиент сразу попадёт в портал заказов и сможет оформлять заказы.
        </p>
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        {/* Left: link + share */}
        <div className="space-y-6">
          <Card className="p-6 md:p-8 bg-card border-[#1A1A1A]/8">
            <div className="flex items-center gap-2 mb-4">
              <LinkIcon className="h-4 w-4 text-[#556B2F]" />
              <h3 className="eyebrow">Пригласительная ссылка</h3>
            </div>
            <div className="flex gap-2">
              <Input
                readOnly
                value={inviteUrl}
                onClick={(e) => e.target.select()}
                data-testid="invite-link"
                className="flex-1 h-12 font-mono text-sm bg-[#F2EFE6]/40 border-[#1A1A1A]/15"
              />
              <Button
                onClick={() => copyToClipboard(inviteUrl, 'Ссылка скопирована')}
                className="cff-button-primary h-12 px-5"
                data-testid="copy-link-btn"
              >
                <Copy className="h-4 w-4 mr-2" /> Копировать
              </Button>
            </div>
          </Card>

          <Card className="p-6 md:p-8 bg-card border-[#1A1A1A]/8">
            <div className="flex items-center gap-2 mb-4">
              <MessageSquare className="h-4 w-4 text-[#556B2F]" />
              <h3 className="eyebrow">Текст сообщения</h3>
            </div>
            <Textarea
              rows={4}
              value={customMsg}
              onChange={(e) => setCustomMsg(e.target.value)}
              className="border-[#1A1A1A]/15 focus:ring-[#556B2F]"
              data-testid="invite-message"
            />
            <div className="flex gap-2 mt-3">
              <Button
                variant="outline"
                size="sm"
                onClick={() => copyToClipboard(fullMessage, 'Полное сообщение скопировано')}
                className="cff-button-outline"
                data-testid="copy-full-btn"
              >
                <Copy className="h-3.5 w-3.5 mr-2" /> Копировать полностью
              </Button>
              {typeof navigator !== 'undefined' && navigator.share && (
                <Button onClick={shareNative} variant="outline" size="sm" className="cff-button-outline" data-testid="native-share-btn">
                  <Share2 className="h-3.5 w-3.5 mr-2" /> Поделиться
                </Button>
              )}
            </div>
          </Card>

          <Card className="p-6 md:p-8 bg-card border-[#1A1A1A]/8">
            <div className="flex items-center gap-2 mb-5">
              <Share2 className="h-4 w-4 text-[#556B2F]" />
              <h3 className="eyebrow">Каналы отправки</h3>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
              {channels.map(c => (
                <button
                  key={c.key}
                  onClick={c.action}
                  data-testid={`share-${c.key}`}
                  className={`${c.bg} ${c.fg} rounded-lg p-4 flex flex-col items-center gap-2 hover:opacity-90 transition-opacity cff-hover-lift`}
                >
                  <c.Icon className="h-5 w-5" />
                  <span className="text-xs font-semibold tracking-wider uppercase">{c.label}</span>
                </button>
              ))}
            </div>
          </Card>
        </div>

        {/* Right: QR code */}
        <Card className="p-6 md:p-10 bg-[#1A1A1A] text-[#F6F4EE] border-0 flex flex-col items-center justify-center relative overflow-hidden" data-testid="qr-card">
          <div className="absolute inset-0 bg-grain opacity-30 pointer-events-none" />
          <div className="flex items-center gap-2 mb-6 self-start relative z-10">
            <QrCode className="h-4 w-4 text-[#C9A352]" />
            <h3 className="eyebrow text-[#C9A352]">QR-код</h3>
          </div>

          <div ref={canvasRef} className="bg-[#F6F4EE] p-6 rounded-xl relative z-10" data-testid="qr-canvas">
            <QRCodeCanvas
              value={inviteUrl}
              size={240}
              level="H"
              fgColor="#1A1A1A"
              bgColor="#F6F4EE"
              includeMargin={false}
            />
            <div className="mt-4 text-center">
              <p className="text-[10px] tracking-widest uppercase text-[#1A1A1A]/50 font-medium">by</p>
              <p className="text-xl font-extrabold text-[#1A1A1A] tracking-tight">
                CRAFT<span className="text-[#C9A352]">FOOD</span>
              </p>
              <p className="text-[8px] tracking-[0.25em] uppercase text-[#1A1A1A]/50 mt-1">{t('tagCraftfood')}</p>
            </div>
          </div>

          <Button
            onClick={downloadQR}
            className="mt-6 cff-button-gold h-11 px-6 relative z-10"
            data-testid="download-qr-btn"
          >
            <Download className="h-4 w-4 mr-2" /> Скачать PNG
          </Button>

          <p className="text-xs text-[#F6F4EE]/40 mt-4 text-center max-w-xs relative z-10">
            Распечатайте на визитке, упаковке или поставьте на витрину магазина — клиенты отсканируют и сразу зарегистрируются.
          </p>
        </Card>
      </div>
    </div>
  );
}
